import React, {
  lazy,
  Suspense,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import "./Home.css";
import CartDrawer from "./Cart/CartDrawer";
import { useCart } from "./Cart/CartContext";
import { Helmet } from "react-helmet-async";
const IntroVideo = lazy(() => import("./Home/IntroVideo"));
const Carousel = lazy(() => import("./Home/Carousel"));
const IntroAnimatedText = lazy(() => import("./Home/IntroAnimatedText"));
const Footer = lazy(() => import("./Home/Footer"));
const AboutUs = lazy(() => import("./AboutUs"));
const Clients = lazy(() => import("./Home/Clients/Clients"));
const Immortal = lazy(() => import("./Home/Immortal/ImmortalHome"));
const CategoryCarouselV2 = lazy(
  () => import("./CategoryCarousel/CategoryCarouselV2"),
);
const CDSection = lazy(() => import("./Home/CDSection/CDSection"));

const Loader = () => <div style={{ height: "350px" }} />;

const toCssSize = (value) => {
  if (typeof value === "number") {
    return `${value}px`;
  }

  return value;
};

const SectionLoader = () => (
  <div className="section-loader" aria-hidden="true" />
);

const LazySectionContent = ({ children, onReady }) => {
  useEffect(() => {
    onReady();
  }, [onReady]);

  return children;
};

const ViewportLazySection = ({
  children,
  desktopHeight = 300,
  mobileHeight = desktopHeight,
  rootMargin = "0px 0px 200px 0px",
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [contentReady, setContentReady] = useState(false);

  const sectionRef = useRef(null);

  const handleContentReady = useCallback(() => {
    setContentReady(true);
  }, []);

  useEffect(() => {
    const element = sectionRef.current;

    if (!element || shouldRender) return;

    // Fallback for older browsers
    if (!("IntersectionObserver" in window)) {
      setShouldRender(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        setShouldRender(true);
        observer.unobserve(entry.target);
      },
      {
        root: null,
        rootMargin,
        threshold: 0.01,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, shouldRender]);

  const reserveStyle = {
    "--lazy-desktop-height": toCssSize(desktopHeight),
    "--lazy-mobile-height": toCssSize(mobileHeight),
  };

  return (
    <div
      ref={sectionRef}
      className={`viewport-lazy-section ${!contentReady ? "is-reserving" : ""}`}
      style={!contentReady ? reserveStyle : undefined}
    >
      {shouldRender ? (
        <Suspense fallback={<SectionLoader />}>
          <LazySectionContent onReady={handleContentReady}>
            {children}
          </LazySectionContent>
        </Suspense>
      ) : (
        <SectionLoader />
      )}
    </div>
  );
};

const INTRO_KEY = "rt_intro_played";
const Home = ({ onReadyForFooter, Ip, hasSeenIntro, setHasSeenIntro }) => {
  const navigate = useNavigate();
  const { cartItems, addToCart, removeItem, increaseQty, decreaseQty } =
    useCart();
  const [animationPhase, setAnimationPhase] = useState("text");

  // const [animationPhase, setAnimationPhase] = useState(
  //   hasSeenIntro ? "text" : "video",
  // );
  // const [carouselReady, setCarouselReady] = useState(true);

  // const handleIntroAnimationComplete = () => {
  //   setAnimationPhase("carousel");
  //   setTimeout(() => setCarouselReady(true), 350);
  // };

  const [carouselReady, setCarouselReady] = useState(false);

  const handleIntroAnimationComplete = () => {
    // Mount the carousel, but do not mount anything below it yet.
    setAnimationPhase("carousel");
  };
  const handleCarouselReady = () => {
    setCarouselReady(true);
  };

  const handleVideoFinish = () => {
    // Hide the intro video and start the text animation.
    localStorage.setItem(INTRO_KEY, "1");
    // setShowIntro(false);
    setHasSeenIntro(true);
    setAnimationPhase("text");
  };

  const [cartOpen, setCartOpen] = useState(false);
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>Right Track</title>

        <meta
          name="description"
          content="Discover vinyl records, classic albums, new releases and collectible editions at Right Track Lebanon. Browse artists, genres and exclusive vinyl online."
        />

        <meta name="robots" content="index, follow, max-image-preview:large" />

        <link rel="canonical" href="https://righttrack.com.lb/" />

        <meta property="og:title" content="Right Track" />

        <meta
          property="og:description"
          content="Discover vinyl records, new releases, classic albums and collectible editions at Right Track Lebanon."
        />

        <meta property="og:url" content="https://righttrack.com.lb/" />

        <meta property="og:type" content="website" />
      </Helmet>
      <div className="home-container">
        {/* Fixed-height intro/carousel area */}
        <section className="home-hero">
          <div
            className={`intro-text-background ${animationPhase === "carousel" ? "background-layer" : "fullscreen"
              }`}
          >
            <Suspense fallback={null}>
              <IntroAnimatedText
                forceBackground={animationPhase === "carousel"}
                onAnimationComplete={handleIntroAnimationComplete}
              />
            </Suspense>
          </div>

          {/* {animationPhase === "carousel" && (
            <div
              id="section-carousel"
              className={`carousel-foreground ${carouselReady ? "visible" : ""
                }`}
            >
              <Suspense fallback={<div className="carousel-reserved-space" />}>
                <Carousel
                  scrollDown="section-about"
                  onReady={handleCarouselReady}
                />
              </Suspense>
            </div>
          )} */}
          <div
            id="section-carousel"
            className={`carousel-foreground ${animationPhase === "carousel" && carouselReady ? "visible" : ""
              }`}
            aria-hidden={animationPhase !== "carousel"}
          >
            <Suspense fallback={<Loader />}>
              <Carousel
                scrollDown="section-about"
                onReady={() => setCarouselReady(true)}
              />
            </Suspense>
          </div>
        </section>

        {/* Nothing below the carousel mounts before it is ready */}
        {animationPhase === "carousel" && carouselReady && (
          <main className="home-after-carousel">
            <div id="section-about" style={{ backdropFilter: "blur(2px)" }}>
              <ViewportLazySection
                desktopHeight={600}
                mobileHeight="clamp(700px, 110svh, 950px)"
              >
                <AboutUs scrollDown="section-immortal" />
              </ViewportLazySection>
            </div>

            <div id="section-immortal">
              <ViewportLazySection
                desktopHeight={600}
                mobileHeight="clamp(500px, 85svh, 750px)"
              >
                <Immortal scrollDown="section-catcarousel" />
              </ViewportLazySection>
            </div>

            <div id="section-catcarousel" className="arabic-vinyl-section">
              <ViewportLazySection height={600}>
                <CategoryCarouselV2
                  id="arabic-vinyl"
                  title="Arabic Vinyl"
                  onAddToCart={(item, qty, sourceEl) =>
                    addToCart(item, qty, sourceEl)
                  }
                  direction="ltr"
                  category="Arabic"
                />
              </ViewportLazySection>

              <ViewportLazySection height={600}>
                <CategoryCarouselV2
                  id="pop-modern"
                  title="Pop Modern"
                  onAddToCart={(item, qty, sourceEl) =>
                    addToCart(item, qty, sourceEl)
                  }
                  direction="ltr"
                />
              </ViewportLazySection>

              <ViewportLazySection height={600}>
                <CategoryCarouselV2
                  id="arabic-vinyl-2"
                  title="All Time Classics Vinyl"
                  onAddToCart={(item, qty, sourceEl) =>
                    addToCart(item, qty, sourceEl)
                  }
                  direction="ltr"
                  category="Selection All Time"
                />
              </ViewportLazySection>

              <ViewportLazySection height={600}>
                <CategoryCarouselV2
                  id="classical-vinyl"
                  title="Classical Music Vinyl"
                  onAddToCart={(item, qty, sourceEl) =>
                    addToCart(item, qty, sourceEl)
                  }
                  direction="ltr"
                  category="Classical"
                />
              </ViewportLazySection>

              <div id="section-cds">
                <ViewportLazySection
                  desktopHeight={600}
                  mobileHeight="clamp(500px, 85svh, 750px)"
                >
                  <CDSection scrollDown="section-catcarousel" />
                </ViewportLazySection>
              </div>

              <ViewportLazySection height={400}>
                <Clients />
              </ViewportLazySection>

              <ViewportLazySection height={400}>
                <Footer />
              </ViewportLazySection>
            </div>



            <CartDrawer
              isOpen={cartOpen}
              onClose={() => setCartOpen(false)}
              items={cartItems}
              onRemoveItem={removeItem}
              onIncreaseQty={increaseQty}
              onDecreaseQty={decreaseQty}
              onViewCart={() => navigate("/cart")}
              onCheckout={() => navigate("/CheckOut")}
              onContinueShopping={() => setCartOpen(false)}
            />
          </main>
        )}
      </div>
    </>
  );
};

export default Home;
